import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "./store";
import { getCurrentUser, setToken } from "./features/auth/authSlice";
import AppRoutes from "./routes";
import { RingLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "./context/ThemeContext";
import { LoadingProvider } from "./context/LoadingContext";
import GlobalLoadingOverlay from "./components/common/GlobalLoadingOverlay";
import { jwtDecode } from 'jwt-decode';
import { clarity } from "react-microsoft-clarity";

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  useEffect(() => {
    // Microsoft Clarity
    if (process.env.REACT_APP_CLARITY_PROJECT_ID) {
      clarity.init(process.env.REACT_APP_CLARITY_PROJECT_ID);
    } else {
      console.warn("CLARITY_PROJECT_ID is not set");
    }

    // Google Tag Manager (gtag.js)
    const gtmId = process.env.REACT_APP_GTM_ID;
    if (gtmId) {
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gtmId}`;
      script.async = true;
      document.head.appendChild(script);

      const scriptConfig = document.createElement("script");
      scriptConfig.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gtmId}');
      `;
      document.head.appendChild(scriptConfig);
    } else {
      console.warn("GTM_ID is not set");
    }

    // Meta Pixel (Facebook Pixel)
    const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
    if (metaPixelId) {
      // fbq initialization script
      const fbqScript = document.createElement("script");
      fbqScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${metaPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbqScript);

      // noscript fallback image
      const noscript = document.createElement("noscript");
      const img = document.createElement("img");
      img.height = 1;
      img.width = 1;
      img.style.display = "none";
      img.src = `https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
      document.body.appendChild(noscript);
    } else {
      console.warn("META_PIXEL_ID is not set");
    }
  }, []);
  // Token varsa her zaman kullanıcı bilgilerini güncelle
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && isAuthenticated) {
      // Her sayfa yenilemesinde kullanıcı bilgilerini API'den al
      dispatch(getCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  // Access token süresi dolmadan otomatik yenile
  useEffect(() => {
    const scheduleRefresh = () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      if (!token || !refreshToken) return;

      try {
        const decoded: any = jwtDecode(token);
        const expSeconds = decoded.exp ? decoded.exp : 0;
        const nowSeconds = Math.floor(Date.now() / 1000);
        const secondsUntilExpiry = expSeconds - nowSeconds;

        // 60 saniye önce yenile
        const refreshInMs = Math.max((secondsUntilExpiry - 60) * 1000, 0);
        if (refreshInMs === 0) return;

        const id = setTimeout(async () => {
          try {
            const res = await fetch((process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1') + '/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken })
            });
            if (res.ok) {
              const data = await res.json();
              if (data.access_token) {
                localStorage.setItem('token', data.access_token);
                dispatch(setToken(data.access_token));
              }
              if (data.refresh_token) {
                localStorage.setItem('refreshToken', data.refresh_token);
              }
              // Bir sonraki yenilemeyi planla
              scheduleRefresh();
            } else {
              // Yenileme başarısızsa (örn. refresh token süresi dolmuşsa) logout yap
              console.warn("Token refresh failed, logging out...");
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              dispatch(setToken(null));
              window.location.href = '/login';
            }
          } catch (_) {
            // sessiz geç
          }
        }, refreshInMs);

        return () => clearTimeout(id);
      } catch (_) {
        // Geçersiz token ise bir şey yapma
      }
    };

    const cleanup = scheduleRefresh();
    return cleanup;
  }, [dispatch, isAuthenticated]);

  return (
    <ThemeProvider>
      <LoadingProvider>
        <GlobalLoadingOverlay />
        {/* Eğer yükleme devam ediyorsa bir yükleme göstergesi göster */}
        {isAuthenticated && isLoading ? (
          <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="text-center">
              <RingLoader color="#007bff" size={60} />
              <p className="mt-3">{t("loading")}</p>
            </div>
          </div>
        ) : (
          <AppRoutes />
        )}
      </LoadingProvider>
    </ThemeProvider>
  );
};

export default App;
